// Doc-level cross-equation symbol hover — the React reader's counterpart
// of the widget's `frontend/src/widget/hooks/crossSymbol.ts`
// (PRD 07 §10.1 + §14.5, PRD 10 Phase B deliverable 3).
//
// Semantics matched from the widget: hover one symbol, every occurrence of
// the SAME symbol across every equation on the page lights up. Before this,
// `EquationBlock` scoped its hover to a single block, so the two reading
// surfaces disagreed on the same document.
//
// Identity is `corpus_symbol_id ?? latex`. `symbol_id` cannot be the key —
// it is minted per-equation as `{eq_id}-s{n}`
// (`backend/parse/equations.py::enumerate_symbols`), so it never matches
// across blocks.
//
// Each block still detects its own hover (`useEquationHover`) and publishes
// the resulting key here. The owner guard is what makes that safe: moving
// the pointer from block A to block B fires A's mouseout and B's mouseover
// in the same commit, and without it A's clear could land after B's set and
// blank the highlight.

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type { EquationSymbol } from "../data/types";

export function symbolKey(symbol: EquationSymbol): string {
  return symbol.corpus_symbol_id ?? symbol.latex;
}

interface SymbolHoverValue {
  hoveredKey: string | null;
  publish: (ownerId: string, key: string | null) => void;
}

// No-op default: an `EquationBlock` rendered without the provider keeps its
// own block-scoped hover (see `activeKey` in EquationBlock).
const SymbolHoverContext = createContext<SymbolHoverValue>({
  hoveredKey: null,
  publish: () => {},
});

export function useEquationSymbolHover(): SymbolHoverValue {
  return useContext(SymbolHoverContext);
}

export function EquationSymbolHoverProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ owner: string | null; key: string | null }>({
    owner: null,
    key: null,
  });
  const publish = useCallback((ownerId: string, key: string | null) => {
    setState((prev) => {
      if (key) return { owner: ownerId, key };
      return prev.owner === ownerId ? { owner: null, key: null } : prev;
    });
  }, []);
  const value = useMemo(() => ({ hoveredKey: state.key, publish }), [state.key, publish]);
  return (
    <SymbolHoverContext.Provider value={value}>{children}</SymbolHoverContext.Provider>
  );
}
